import { marked } from "marked";
import { useEffect, useMemo, useRef } from "react";

// Guide sections are GitHub-flavoured markdown. Raw HTML in them is shown as
// text, not rendered, so what reaches dangerouslySetInnerHTML is only what
// marked itself emits.
const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[char]!);

marked.use({ renderer: { html: ({ text }) => escapeHtml(text) } });

/**
 * Markdown to HTML. Task-list boxes become live checkboxes numbered in
 * document order (data-checkbox), checked as the player left them; tables
 * get a scrolling wrapper.
 */
export function renderMarkdown(markdown: string, savedCheckboxState: Record<string, boolean>): string {
  const doc = new DOMParser().parseFromString(marked.parse(markdown, { async: false }), "text/html");
  doc.querySelectorAll("table").forEach((table) => {
    const wrap = doc.createElement("div");
    wrap.className = "table-wrap";
    table.replaceWith(wrap);
    wrap.append(table);
  });
  doc.querySelectorAll<HTMLInputElement>('li > input[type="checkbox"]').forEach((input, index) => {
    const item = input.parentElement!;
    const label = doc.createElement("label");
    label.className = "check";
    const text = doc.createElement("span");
    const nodes = Array.from(item.childNodes).filter((node) => node !== input);
    if (nodes[0]?.nodeType === Node.TEXT_NODE) nodes[0].textContent = nodes[0].textContent!.trimStart();
    text.append(...nodes);
    const checkedInFile = input.hasAttribute("checked");
    input.removeAttribute("disabled");
    input.removeAttribute("checked");
    input.dataset.checkbox = String(index);
    if (savedCheckboxState[index] ?? checkedInFile) input.setAttribute("checked", "");
    label.append(input, text);
    item.replaceChildren(label);
  });
  return doc.body.innerHTML;
}

export function MarkdownRenderer({
  markdown,
  onCheckboxChange,
  savedCheckboxState,
}: {
  markdown: string;
  onCheckboxChange: (index: number, checked: boolean) => void;
  savedCheckboxState: Record<string, boolean>;
}) {
  const html = useMemo(() => renderMarkdown(markdown, savedCheckboxState), [markdown, savedCheckboxState]);
  const content = useRef<HTMLDivElement>(null);
  // The checkboxes come from innerHTML, not React, so React's onChange
  // never fires for them; a DOM listener on the container catches them.
  useEffect(() => {
    const element = content.current;
    if (!element) return;
    const onChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const index = Number(target.dataset.checkbox ?? -1);
      if (target.type === "checkbox" && index >= 0) onCheckboxChange(index, target.checked);
    };
    element.addEventListener("change", onChange);
    return () => element.removeEventListener("change", onChange);
  }, [onCheckboxChange]);
  return <div ref={content} className="guide-content" dangerouslySetInnerHTML={{ __html: html }} />;
}
